import { useEffect, useState } from 'react'
import { toast } from 'react-toastify'
import { AdminApi, AdminMisaInvoiceLog } from '../../services/adminApi'
import { formatDbDateTimeVi } from '../../utils/dbDateDisplay'

const fmtMoney = (v: number) => `${Number(v || 0).toLocaleString('vi-VN')} đ`

const AdminMisaInvoiceLogsPage = () => {
  const [status, setStatus] = useState<'PENDING' | 'SUCCESS' | 'FAIL' | ''>('')
  const [page, setPage] = useState(1)
  const [totalPages, setTotalPages] = useState(0)
  const [items, setItems] = useState<AdminMisaInvoiceLog[]>([])
  const [loading, setLoading] = useState(false)

  const loadData = async () => {
    setLoading(true)
    try {
      const res = await AdminApi.getMisaLogs({ page, limit: 20, status: status || undefined })
      setItems(res.data.items)
      setTotalPages(res.data.pagination.totalPages)
    } catch {
      toast.error('Không tải được log MISA')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    loadData()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, status])

  const retry = async (logId: number) => {
    try {
      await AdminApi.retryMisaLog(logId)
      toast.success('Đã gửi retry MISA')
      loadData()
    } catch {
      toast.error('Retry thất bại')
    }
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h1 className="text-xl font-semibold text-m2-on-surface">Hóa đơn MISA</h1>
        <select
          className="rounded border border-m2-outline px-3 py-2 text-sm"
          value={status}
          onChange={(e) => {
            setPage(1)
            setStatus(e.target.value as 'PENDING' | 'SUCCESS' | 'FAIL' | '')
          }}
        >
          <option value="">Tất cả trạng thái</option>
          <option value="PENDING">PENDING</option>
          <option value="SUCCESS">SUCCESS</option>
          <option value="FAIL">FAIL</option>
        </select>
      </div>

      <div className="overflow-hidden rounded-lg border border-m2-outline-variant bg-m2-surface-container">
        <div className="overflow-x-auto">
          <table className="min-w-full text-sm">
            <thead className="bg-m2-surface-low text-left text-xs uppercase text-m2-on-surface-variant">
              <tr>
                <th className="px-3 py-2">Thời gian</th>
                <th className="px-3 py-2">Loại</th>
                <th className="px-3 py-2">DN</th>
                <th className="px-3 py-2">Tổng tiền HĐ</th>
                <th className="px-3 py-2">Trạng thái</th>
                <th className="px-3 py-2">Số HĐ / Mã CQT</th>
                <th className="px-3 py-2">Lỗi</th>
                <th className="px-3 py-2">Thao tác</th>
              </tr>
            </thead>
            <tbody>
              {loading && (
                <tr>
                  <td colSpan={8} className="px-3 py-4 text-center text-m2-on-surface-variant">
                    Đang tải...
                  </td>
                </tr>
              )}
              {!loading &&
                items.map((row) => (
                  <tr key={row.id} className="border-t border-m2-outline-variant">
                    <td className="px-3 py-2 text-xs text-m2-on-surface">{formatDbDateTimeVi(row.created_at)}</td>
                    <td className="px-3 py-2 text-xs text-m2-on-surface">{row.scope}</td>
                    <td className="px-3 py-2 text-xs text-m2-on-surface">{row.enterprise_name || '-'}</td>
                    <td className="px-3 py-2 text-xs font-medium text-m2-on-surface">{fmtMoney(row.invoice_amount_total)}</td>
                    <td className="px-3 py-2 text-xs">
                      <span
                        className={`rounded px-2 py-1 ${
                          row.status === 'SUCCESS'
                            ? 'bg-m2-secondary/15 text-green-700'
                            : row.status === 'FAIL'
                            ? 'bg-red-100 text-red-700'
                            : 'bg-yellow-100 text-yellow-700'
                        }`}
                      >
                        {row.status}
                      </span>
                    </td>
                    <td className="px-3 py-2 text-xs text-m2-on-surface">
                      <div>{row.misa_inv_no || '-'}</div>
                      <div>{row.misa_inv_code || '-'}</div>
                    </td>
                    <td className="max-w-xs px-3 py-2 text-xs text-red-600">{row.error_message || '-'}</td>
                    <td className="px-3 py-2">
                      {row.status === 'FAIL' || (row.status === 'SUCCESS' && !row.misa_inv_no) ? (
                        <button className="rounded border border-primary-600 px-2 py-1 text-xs text-m2-primary" onClick={() => retry(row.id)}>
                          Thử lại
                        </button>
                      ) : (
                        <span className="text-xs text-m2-outline">-</span>
                      )}
                    </td>
                  </tr>
                ))}
              {!loading && items.length === 0 && (
                <tr>
                  <td colSpan={8} className="px-3 py-4 text-center text-m2-on-surface-variant">
                    Không có dữ liệu
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      <div className="flex items-center justify-end gap-2">
        <button className="rounded border border-m2-outline px-3 py-1 text-sm" disabled={page <= 1} onClick={() => setPage((p) => p - 1)}>
          Trước
        </button>
        <span className="text-sm text-m2-on-surface-variant">
          Trang {page} / {totalPages || 1}
        </span>
        <button className="rounded border border-m2-outline px-3 py-1 text-sm" disabled={page >= totalPages} onClick={() => setPage((p) => p + 1)}>
          Sau
        </button>
      </div>
    </div>
  )
}

export default AdminMisaInvoiceLogsPage

